import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import {
    HistoryCursor,
    PrepaidCardHistoryDirection,
    TransactionFilterType,
} from '@models/prepaidCard';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

const PAGE_SIZE = 20;

function getDirection(filter: TransactionFilterType): PrepaidCardHistoryDirection | undefined {
    if (filter === 'DEDUCTION') return 'DEBIT';
    if (filter === 'CHARGE') return 'CREDIT';
    return undefined;
}

function usePrepaidCardHistories(id: string) {
    const [filter, setFilter] = useState<TransactionFilterType>('ALL');
    const direction = getDirection(filter);
    const params = {
        direction,
        size: PAGE_SIZE,
    };

    const queryInfo = useInfiniteQuery({
        queryKey: userPrepaidCardQueries.histories(id, params).queryKey,
        queryFn: ({ pageParam }) => PrepaidCardApi.getHistories(id, {
            ...params,
            cursorTransactedAt: pageParam?.cursorTransactedAt,
            cursorId: pageParam?.cursorId,
        }),
        initialPageParam: undefined as HistoryCursor | undefined,
        getNextPageParam: (lastPage) => {
            if (!lastPage.hasNext
                || !lastPage.nextCursorTransactedAt
                || !lastPage.nextCursorId) {
                return undefined;
            }
            return {
                cursorTransactedAt: lastPage.nextCursorTransactedAt,
                cursorId: lastPage.nextCursorId,
            };
        },
    });

    const histories = useMemo(
        () => queryInfo.data?.pages.flatMap((page) => page.histories) ?? [],
        [queryInfo.data?.pages],
    );

    return {
        ...queryInfo,
        histories,
        filter,
        setFilter,
    };
}

export default usePrepaidCardHistories;
